import { useSyncExternalStore } from "react";

/** 인력 자원과 계정의 연결. 이메일이 같아도 승인 전에는 붙지 않는다. */
export type ResourceLinkChip = "계정 없음" | "초대 중" | "연결 제안" | "연결됨" | "확인 필요";

export type ResourceAccount = { id: string; name: string; email: string };

export type ResourceLinkRow = { resourceId: string; accountId: string; status: "초대 중" | "연결됨" };

export type AssigneeSnapshot = { resourceId: string; accountId: string | null; name: string };

type ResourceRef = { id: string; displayName: string; email: string };

let links: ResourceLinkRow[] = [];
let version = 0;
const listeners = new Set<() => void>();

function emit() {
  version += 1;
  for (const listener of listeners) listener();
}

function norm(email: string): string {
  return email.trim().toLowerCase();
}

export function resetResourceLinks(): void {
  links = [];
  emit();
}

export function getResourceLinks(): ResourceLinkRow[] {
  return links;
}

export function subscribeResourceLinks(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useResourceLinks(): number {
  return useSyncExternalStore(subscribeResourceLinks, () => version, () => version);
}

export function accountByEmail(resource: ResourceRef, accounts: ResourceAccount[]): ResourceAccount | undefined {
  const email = norm(resource.email);
  if (!email) return undefined;
  return accounts.find((account) => norm(account.email) === email);
}

export function accountByNameMismatch(resource: ResourceRef, accounts: ResourceAccount[]): ResourceAccount | undefined {
  const email = norm(resource.email);
  const name = resource.displayName.trim();
  if (!name) return undefined;
  return accounts.find((account) => account.name.trim() === name && norm(account.email) !== email);
}

/** 저장된 연결이 없으면 제안·확인으로만 남긴다. */
export function resourceLinkChip(resource: ResourceRef, accounts: ResourceAccount[], rows: ResourceLinkRow[] = links): ResourceLinkChip {
  const row = rows.find((item) => item.resourceId === resource.id);
  if (row?.status === "연결됨") return "연결됨";
  if (row?.status === "초대 중") return "초대 중";
  if (accountByEmail(resource, accounts)) return "연결 제안";
  if (accountByNameMismatch(resource, accounts)) return "확인 필요";
  return "계정 없음";
}

/** 이메일이 같은 제안만 승인한다. 다르면 연결하지 않는다. */
export function approveSameEmail(resource: ResourceRef, accounts: ResourceAccount[]): boolean {
  const account = accountByEmail(resource, accounts);
  if (!account) return false;
  links = [...links.filter((item) => item.resourceId !== resource.id), { resourceId: resource.id, accountId: account.id, status: "연결됨" }];
  emit();
  return true;
}

/** 이메일이 다른 쌍은 관리자가 확인한 뒤에만 연결한다. */
export function confirmMismatch(resource: ResourceRef, account: ResourceAccount): boolean {
  if (!accountByNameMismatch(resource, [account]) && norm(resource.email) === norm(account.email)) return false;
  if (norm(resource.email) === norm(account.email)) return false;
  links = [...links.filter((item) => item.resourceId !== resource.id), { resourceId: resource.id, accountId: account.id, status: "연결됨" }];
  emit();
  return true;
}

export function inviteResourceAccount(resourceId: string): void {
  links = [...links.filter((item) => item.resourceId !== resourceId), { resourceId, accountId: "", status: "초대 중" }];
  emit();
}

/** 연결만 끊는다. 배정은 건드리지 않는다. */
export function unlinkResource(resourceId: string): void {
  links = links.filter((item) => item.resourceId !== resourceId);
  emit();
}

import { rememberName } from "./name-snapshot";

/** 담당자 기록은 그 시점의 이름을 고정한다. */
export function freezeAssignee(resource: ResourceRef, accountId: string | null): AssigneeSnapshot {
  rememberName(resource.id, resource.displayName);
  return { resourceId: resource.id, accountId, name: resource.displayName };
}

export function assigneeLabel(snapshot: AssigneeSnapshot): string {
  return snapshot.name;
}
